import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Sparkles, LogOut, Flame, Bell, Menu, X } from "lucide-react";

/**
 * Site header. Desktop keeps the horizontal nav; mobile (< md, 768px) uses a
 * slide-in drawer so the entire authed navigation stays reachable on phones.
 */
export default function Header() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [showBell, setShowBell] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const active = (p) => (pathname === p ? "text-[#722f37]" : "text-[#1a1a1a] hover:text-[#722f37]");

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    if (user) loadNotifs();
  }, [user?.id, user?.unread_notifications]);

  // Close drawer whenever the route changes so tapping a link dismisses it.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  // Prevent body scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const toggleBell = async () => {
    const next = !showBell;
    setShowBell(next);
    if (next && (user?.unread_notifications || 0) > 0) {
      try {
        await api.post("/notifications/mark-read");
        refresh();
      } catch { /* silent */ }
    }
  };

  const authedLinks = [
    ["/discover", "Discover", "nav-discover"],
    ["/top-rated", "Top rated", "nav-top-rated"],
    ["/potential-matches", "Golden", "nav-potential"],
    ["/rated-by-you", "Rated by you", "nav-rated-by-you"],
    ["/matches", "Matches", "nav-matches"],
    ["/inbox", "Inbox", "nav-inbox"],
    ["/profile", "My Profile", "nav-profile"],
    ["/pricing", "Membership", "nav-pricing"],
  ];

  return (
    <header className="glass sticky top-0 z-40" data-testid="site-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 md:px-10 py-3 md:py-4 gap-2">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          <Sparkles className="w-5 h-5 text-[#c39d2b]" />
          <span className="font-serif-display text-xl sm:text-2xl tracking-tight text-[#722f37] whitespace-nowrap">
            Prime Connect
          </span>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-sm" data-testid="site-nav-desktop">
          {user ? (
            <>
              {authedLinks.map(([to, label, tid]) => (
                <Link key={to} to={to} className={active(to)} data-testid={tid}>{label}</Link>
              ))}
              {user.is_admin && <Link to="/admin" className={active("/admin")} data-testid="nav-admin">Admin</Link>}
            </>
          ) : (
            <>
              <a href="#how" className="hover:text-[#722f37]" data-testid="nav-how">How it works</a>
              <a href="#pricing" className="hover:text-[#722f37]" data-testid="nav-pricing-anchor">Pricing</a>
              <Link to="/advice" className="hover:text-[#722f37]" data-testid="nav-advice">Advice</Link>
            </>
          )}
        </nav>

        {/* Right-hand controls (desktop + notification bell always visible on mobile too) */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {user ? (
            <>
              <span className="hidden lg:block text-sm text-[#5c5c5c]" data-testid="header-user-name">
                {user.display_name}
              </span>
              {(user.rating_streak || 0) > 0 && (
                <span
                  className="hidden md:inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-[#c39d2b]/15 text-[#8a6a10] border border-[#c39d2b]/40"
                  title={`${user.rating_streak}-day rating streak · +1 credit every 7 days`}
                  data-testid="header-streak"
                >
                  <Flame className="w-3.5 h-3.5" /> {user.rating_streak}d
                </span>
              )}

              {/* Notifications bell (mobile + desktop) */}
              <div className="relative">
                <button
                  onClick={toggleBell}
                  className="relative w-10 h-10 rounded-full border border-[#d6cfc0] hover:border-[#722f37] flex items-center justify-center transition-colors"
                  aria-label="Notifications"
                  data-testid="notif-bell"
                >
                  <Bell className="w-4 h-4 text-[#722f37]" />
                  {(user.unread_notifications || 0) > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#722f37] text-white text-[10px] font-bold flex items-center justify-center px-1"
                      data-testid="notif-badge"
                    >
                      {user.unread_notifications}
                    </span>
                  )}
                </button>
                {showBell && (
                  <div
                    className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-h-96 overflow-auto rounded-2xl bg-white border border-[#e5e0d8] card-elev p-3 z-50"
                    data-testid="notif-panel"
                  >
                    {notifs.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[#5c5c5c]">No notifications yet.</div>
                    ) : (
                      <div className="space-y-2">
                        {notifs.map((n) => (
                          <div
                            key={n.id}
                            className={`p-3 rounded-xl border text-sm ${
                              n.read ? "border-[#e5e0d8] bg-white" : "border-[#c39d2b]/40 bg-[#c39d2b]/10"
                            }`}
                            data-testid={`notif-${n.kind}`}
                          >
                            <div className="font-medium text-[#1a1a1a]">{n.message}</div>
                            <div className="mt-1 text-[10px] uppercase tracking-wider text-[#5c5c5c]">{n.kind}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sign out — desktop only. Mobile users sign out from the drawer. */}
              <button
                onClick={() => { logout(); nav("/"); }}
                className="hidden md:inline-flex btn-outline !py-2 !px-4 items-center gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>

              {/* Hamburger — mobile only */}
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="md:hidden w-10 h-10 rounded-full border border-[#d6cfc0] hover:border-[#722f37] flex items-center justify-center"
                aria-label="Open menu"
                data-testid="nav-hamburger"
              >
                <Menu className="w-5 h-5 text-[#722f37]" />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="btn-outline !py-2 !px-2.5 sm:!px-5 text-sm sm:text-base whitespace-nowrap"
                data-testid="header-login-btn"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="btn-primary !py-2 !px-2.5 sm:!px-5 text-sm sm:text-base whitespace-nowrap"
                data-testid="header-signup-btn"
              >
                Join free
              </Link>
              {/* Advice link — visible on mobile since unauth users need to find it too. */}
              <Link
                to="/advice"
                className="md:hidden text-xs sm:text-sm text-[#722f37] font-semibold px-1 sm:px-2 whitespace-nowrap"
                data-testid="nav-advice-mobile"
              >
                Advice
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer (authed) */}
      {user && drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="nav-drawer-backdrop"
        >
          <aside
            className="absolute right-0 top-0 bottom-0 w-[82%] max-w-[340px] bg-white shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="nav-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e0d8]">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#c39d2b]" />
                <span className="font-serif-display text-lg text-[#722f37]">Prime Connect</span>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-[#faf9f6] flex items-center justify-center"
                aria-label="Close menu"
                data-testid="nav-drawer-close"
              >
                <X className="w-5 h-5 text-[#5c5c5c]" />
              </button>
            </div>

            <div className="px-5 py-4 border-b border-[#e5e0d8]">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#722f37]">Signed in as</div>
              <div className="mt-1 text-base font-semibold text-[#1a1a1a]" data-testid="drawer-user-name">
                {user.display_name}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {(user.rating_streak || 0) > 0 && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold bg-[#c39d2b]/15 text-[#8a6a10] border border-[#c39d2b]/40"
                    data-testid="drawer-streak"
                  >
                    <Flame className="w-3 h-3" /> {user.rating_streak}-day streak
                  </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 bg-[#faf9f6] border border-[#e5e0d8] text-[#5c5c5c]">
                  {user.reveal_credits || 0} credit{(user.reveal_credits || 0) === 1 ? "" : "s"}
                </span>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-3" data-testid="drawer-nav">
              {authedLinks.map(([to, label, tid]) => (
                <Link
                  key={to}
                  to={to}
                  className={`block px-5 py-3 text-base ${
                    pathname === to
                      ? "bg-[#722f37]/5 text-[#722f37] font-semibold border-l-4 border-[#722f37]"
                      : "text-[#1a1a1a] hover:bg-[#faf9f6] border-l-4 border-transparent"
                  }`}
                  data-testid={`drawer-${tid}`}
                >
                  {label}
                </Link>
              ))}
              <Link
                to="/advice"
                className={`block px-5 py-3 text-base ${
                  pathname === "/advice"
                    ? "bg-[#722f37]/5 text-[#722f37] font-semibold border-l-4 border-[#722f37]"
                    : "text-[#1a1a1a] hover:bg-[#faf9f6] border-l-4 border-transparent"
                }`}
                data-testid="drawer-nav-advice"
              >
                Advice
              </Link>
              {user.is_admin && (
                <Link
                  to="/admin"
                  className={`block px-5 py-3 text-base ${
                    pathname === "/admin"
                      ? "bg-[#722f37]/5 text-[#722f37] font-semibold border-l-4 border-[#722f37]"
                      : "text-[#1a1a1a] hover:bg-[#faf9f6] border-l-4 border-transparent"
                  }`}
                  data-testid="drawer-nav-admin"
                >
                  Admin
                </Link>
              )}
            </nav>

            <div className="px-5 py-4 border-t border-[#e5e0d8]">
              <button
                onClick={() => { setDrawerOpen(false); logout(); nav("/"); }}
                className="btn-outline w-full flex items-center justify-center gap-2 !py-3"
                data-testid="drawer-logout"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}
